import { ButtonDropdown, ButtonV2, FilterV2, SearchV2, SortV2, StandardTabsV2 } from '@cimplico/components';
import { useRef, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useCustomWorksheetTemplatesOverview } from '../queries/use-custom-worksheet-templates-overview';
import { CustomWorksheetTemplateOverview, CustomWorksheetTemplateStatus } from '../types/custom-worksheet-v2';
import PageContainer from '../components/page-container/PageContainer';
import PageTitle from '../components/PageTitle';
import CustomWorksheetTemplatesTable from '../features/custom-worksheet-templates/CustomWorksheetTemplatesTable';
import FileInput from '../features/files/FileInput';
import { useCreateCustomWorksheetTemplate } from '../queries/use-create-custom-worksheet-template';
import { toastNotification } from '../components/toast/Toast';
import { useIsFirmRole } from '../hooks/use-is-firm-role';
import { useFirmContext } from '../providers/FirmProvider';

const tabs = ['all', 'published', 'unpublished', 'archived'];

function CustomWorksheetTemplatesPage(): JSX.Element {
    const [search, setSearch] = useState<string>();
    const [params, setParams] = useSearchParams();
    const importRef = useRef<HTMLInputElement | null>(null);
    const currentTab = params.get('tab') ?? 'all';
    const [tab, setTab] = useState(Math.max(tabs.indexOf(currentTab), 0));
    const { isExternalUser } = useIsFirmRole();
    const { shortId } = useFirmContext();

    const [, setSelectedTemplates] = useState<CustomWorksheetTemplateOverview[]>([]);

    const navigate = useNavigate();
    const overviewQuery = useCustomWorksheetTemplatesOverview();
    const createTemplateQuery = useCreateCustomWorksheetTemplate();

    function handleTabChange(idx: number): void {
        setTab(idx);
        params.set('tab', tabs[idx]);
        setParams(params);
    }

    const published = overviewQuery.data?.published ?? 0;
    const unpublished = overviewQuery.data?.unpublished ?? 0;
    const archived = overviewQuery.data?.archived ?? 0;
    const all = published + unpublished + archived;

    async function handleImport(files: File[]): Promise<void> {
        const file = files[0];

        if (!file) {
            return;
        }

        const contents = await file.text();

        let name, description, schema, entityTypes, allowAsSchedule, behaviour;
        let tags: string[] = [];

        try {
            const parsed = JSON.parse(contents);

            if (!parsed.name || parsed.description === undefined || !parsed.tags || !parsed.schema) {
                throw new Error('Invalid import');
            }

            // The below checks are to support an older import version
            const _behaviour = parsed.behaviour !== undefined ? parsed.behaviour : parsed.accountBehaviour ?? undefined;
            const _allowAsSchedule =
                parsed.allowAsSchedule !== undefined ? parsed.allowAsSchedule : parsed.type ? parsed.type === 'CUSTOM_WORKSHEET' : false;

            name = parsed.name;
            description = parsed.description;
            tags = parsed.tags;
            entityTypes = parsed.entityTypes;
            schema = parsed.schema;
            allowAsSchedule = _allowAsSchedule;
            behaviour = _behaviour;
        } catch (error) {
            toastNotification('error', 'Invalid import');
        }

        createTemplateQuery.mutate(
            {
                name: name?.trim(),
                description: (description || null)?.trim(),
                tags: tags.map((t) => t.trim()).filter((t) => t),
                schema,
                entityTypes,
                allowAsSchedule,
                behaviour,
            },
            {
                onSuccess: (res) => {
                    toastNotification('success', 'Successfully created template');
                    navigate(`${res.template.id}`);
                },
                onError: (err) => {
                    let description = '';

                    if (err.response?.data) {
                        const data = err.response.data;
                        Object.keys(data).forEach((key) => {
                            const messages = data[key];
                            description += messages.join(' ');
                        });
                    }

                    toastNotification('error', 'Unable to create template', description);
                },
            }
        );
    }

    if (isExternalUser) {
        return <Navigate to={`/${shortId}/dashboard`} />;
    }

    return (
        <PageContainer>
            <FileInput innerRef={importRef} onSelect={handleImport} fileTypes={['.cw']} />
            <div className="flex items-center">
                <PageTitle title="Calculation Templates" />
                <div className="flex items-center gap-2 ml-auto">
                    <ButtonDropdown size="sm" label="New Template" onClick={(): void => navigate('new')} variant={'primary'}>
                        <ButtonDropdown.SecondaryAction label="Import" onClick={(): void => importRef.current?.click()} />
                    </ButtonDropdown>
                </div>
            </div>
            <div className="my-4 border-b">
                <StandardTabsV2 selectedIndex={tab} name="template-status" onChange={handleTabChange}>
                    <StandardTabsV2.Tab label={`All (${all})`} />
                    <StandardTabsV2.Tab label={`Published (${published})`} />
                    <StandardTabsV2.Tab label={`Unpublished (${unpublished})`} />
                    <StandardTabsV2.Tab label={`Archived (${archived})`} />
                </StandardTabsV2>
            </div>
            <div className="flex gap-2 items-center">
                <FilterV2 onChange={console.log}>
                    <></>
                </FilterV2>
                <SearchV2 placeholder="Search name..." onChange={(v): void => setSearch(v || undefined)} size="regular" />
                <SortV2 onChange={console.log} options={[{ label: 'Name', value: 'name' }]} />
            </div>
            <div className="flex flex-col items-center mt-4">
                <CustomWorksheetTemplatesTable
                    search={search}
                    status={tabs[tab].toUpperCase() as CustomWorksheetTemplateStatus}
                    onSelect={setSelectedTemplates}
                />
            </div>
        </PageContainer>
    );
}

export default CustomWorksheetTemplatesPage;
